import { useVerifiedCustomer } from "@expert/sales-tools";
import { useMemo } from "react";

import { useCustomerProgram } from "@/customer-info";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { callbackStateToLog, useAgentSdk, useCallbackState, useLogger, useSessionId, VoiceTask } from "@/sdk";
import { useSessionSummaryStore } from "@/session-summary";
import { SummaryResolution } from "@/shared-types";
import { sendEmptySurveyData } from "@/surveys/api/client";

import { useControlsStore } from "../controls.store";

export const selectProgramName = (
    useCustomerAgreementProgramName: boolean,
    wasClaimFiled: boolean,
    partnerConfigProgramName: string | undefined,
    customerAgreementProgramName: string | undefined,
    excludedPrograms: string[],
): string | undefined => {
    const programName = useCustomerAgreementProgramName ? customerAgreementProgramName : partnerConfigProgramName;
    const isExcludedProgram = excludedPrograms.some(
        (excludedProgram) => excludedProgram.toLowerCase() === programName?.toLowerCase(),
    );

    if (isExcludedProgram || !programName) {
        return undefined;
    }
    return wasClaimFiled ? `${programName}-retail` : programName;
};

export const shouldSendSurveyData = ({
    customerLeftTheConference,
    hasCallback,
    isConference,
    conferencePanelOpen,
}: {
    customerLeftTheConference: boolean;
    hasCallback: boolean;
    isConference: boolean;
    conferencePanelOpen: boolean;
}): boolean => {
    const activeConference = isConference && conferencePanelOpen;

    return !(hasCallback || activeConference || customerLeftTheConference);
};

export const buildAutomaticSurveyData = ({
    shouldShowResolutionMenu,
    resolutionStatus,
    isClaimEligible,
    claimFiled,
}: {
    shouldShowResolutionMenu: boolean;
    resolutionStatus: SummaryResolution;
    isClaimEligible: boolean;
    claimFiled: boolean;
}): { resolution?: SummaryResolution; claimFiled?: boolean } => {
    return {
        ...(shouldShowResolutionMenu && { resolution: resolutionStatus }),
        ...(isClaimEligible && { claimFiled }),
    };
};

export const useSurveys = ({
    task,
    shouldShowResolutionMenu,
}: {
    task: VoiceTask;
    shouldShowResolutionMenu: boolean;
}) => {
    const callbackState = useCallbackState();
    const isConference = useControlsStore((state) => state.isConference);
    const conferencePanelOpen = useControlsStore((state) => state.conferencePanelOpen);
    const hasCallback = !!callbackState;

    const { surveyEnabled: partnerSurveyEnabled } = usePartnerFeatureConfigured();
    const { survey } = usePartnerConfig() ?? {};

    const agentSdk = useAgentSdk();
    const logger = useLogger({ module: "useSurveys" });
    const sessionId = useSessionId();
    const customer = useVerifiedCustomer(sessionId);

    const { customerProgram } = useCustomerProgram();
    const isClaimEligible = useMemo(() => {
        const isClaimEligibleProgram = customerProgram?.claimEligible ?? false;
        const isPartnerClaimEligible = survey?.isClaimsEligible ?? false;
        return isClaimEligibleProgram || isPartnerClaimEligible;
    }, [customerProgram, survey]);

    const surveyableResolutionStatuses: SummaryResolution[] = useMemo(() => {
        const baseStatuses: SummaryResolution[] = ["resolved"];
        const shouldSurveyNotResolved = survey?.shouldSurveyNotResolved ?? false;

        if (shouldSurveyNotResolved) {
            baseStatuses.push("not resolved");
        }

        return baseStatuses;
    }, [survey]);

    const isSurveyEligible = (resolutionStatus: SummaryResolution) => {
        if (!partnerSurveyEnabled) {
            logger.info("Surveys are not enabled for this partner, skipping survey eligibility check.");
            return false;
        }

        /* Conditions for transfer to survey:
            1. Does not have a callback scheduled
            2. Does not have an active conference
            3. Customer has not already left after conference
            4. Resolution status is eligible for survey (controlled by feature flags)
        */
        const isSurveyable = shouldSendSurveyData({
            customerLeftTheConference: task.customerLeftTheConference,
            hasCallback,
            isConference,
            conferencePanelOpen,
        });
        const isResolutionSurveyable = surveyableResolutionStatuses.includes(resolutionStatus);

        const surveyIsEligible = isSurveyable && isResolutionSurveyable;

        logger.info(
            {
                surveyIsEligible,
                customerLeftTheConference: task.customerLeftTheConference,
                callbackState: callbackState && callbackStateToLog(callbackState),
                isConference,
                conferencePanelOpen,
                resolutionStatus: resolutionStatus ?? "not resolved",
                surveyableResolutionStatuses,
                partnerSurveyEnabled,
                customerProgram,
            },
            "Is survey eligible",
        );

        return surveyIsEligible;
    };

    /** Returns true when the caller should hang up. A survey transfer owns the call, so those paths return false. */
    const handleSurveyResolution = async (
        resolutionStatus: SummaryResolution = "not resolved",
        hideResolutionStatusForm = false,
        claimFiled = false,
    ): Promise<boolean> => {
        if (resolutionStatus && shouldShowResolutionMenu) {
            useSessionSummaryStore.getState().setResolved(resolutionStatus, { hideResolutionStatusForm });
        }
        const shouldSendSurvey = shouldSendSurveyData({
            customerLeftTheConference: task.customerLeftTheConference,
            hasCallback,
            isConference,
            conferencePanelOpen,
        });

        if (agentSdk.areSurveysAutomatic(task) && shouldSendSurvey) {
            await agentSdk.sendToSurvey(
                task,
                buildAutomaticSurveyData({
                    shouldShowResolutionMenu,
                    resolutionStatus,
                    isClaimEligible,
                    claimFiled,
                }),
            );
            return false;
        }

        const useCustomerAgreementProgramName = survey?.useCustomerAgreementProgram ?? false;
        const customerAgreementProgramName = customer?.productName;
        const excludedPrograms = survey?.excludedPrograms ?? [];
        const ignoreProgram = survey?.ignoreProgram ?? false;

        const programName = ignoreProgram
            ? "NA"
            : selectProgramName(
                  useCustomerAgreementProgramName,
                  claimFiled,
                  customerProgram?.name,
                  customerAgreementProgramName,
                  excludedPrograms,
              );

        const isProgramEligible = Boolean((customerProgram || useCustomerAgreementProgramName) && programName);
        const shouldSendToSurvey = isSurveyEligible(resolutionStatus) && (isProgramEligible || ignoreProgram);

        logger.info(
            {
                shouldSendToSurvey,
                isProgramEligible,
                programName,
                ignoreProgram,
            },
            "Should send to survey",
        );

        if (shouldSendToSurvey) {
            await agentSdk.sendToSurvey(task);
            return false;
        }

        if (!hasCallback) {
            void sendEmptySurveyData(task, "NOT_ELIGIBLE_FOR_SURVEY");
        }

        return true;
    };

    return {
        handleSurveyResolution,
        isClaimEligible,
    };
};
